/**
 * BimEngine720 - 独立的 720° 全景引擎
 * 轻量级独立类，不依赖 3D 引擎的 UI 管理器（toolbar、measure 等）
 * 用法：import { BimEngine720 } from 'iflow-engine'
 */
import { Engine720 } from './components/engine-720';
import type { PanoramaLoadOptions } from './components/engine-720/types';
import { ManagerRegistry } from './core/manager-registry';
import { localeManager } from './services/locale';
import { themeManager } from './services/theme';
import type { LocaleType } from './locales/types';
import type { ThemeType } from './themes/types';
import type { EngineEvents } from './types/events';
import './iflow-engine-base.css';

/**
 * BimEngine720 构造选项
 * 合并引擎配置与主题/语言设置
 */
export interface BimEngine720Options {
    /** 语言 */
    locale?: LocaleType;
    /** 主题 */
    theme?: ThemeType;
    /** 视场角（默认 75） */
    fov?: number;
    /** 是否启用缩放（默认 true） */
    enableZoom?: boolean;
    /** 是否启用旋转（默认 true） */
    enableRotate?: boolean;
    /** 球体半径（默认 500） */
    sphereRadius?: number;
    /** 旋转速度 */
    rotateSpeed?: number;
    /** 缩放速度 */
    zoomSpeed?: number;
    /** 是否启用阻尼 */
    enableDamping?: boolean;
    /** 阻尼因子 */
    dampingFactor?: number;
    /** 最小视场角 */
    minFov?: number;
    /** 最大视场角 */
    maxFov?: number;
}

/**
 * 独立 720° 全景引擎
 *
 * 内部复用 Engine720 组件，跳过 Manager 层
 * 构造时自动初始化，可直接调用 loadPanorama() 加载全景图
 */
export class BimEngine720 {
    public container: HTMLElement;
    private registry: ManagerRegistry;
    private engineComponent: Engine720 | null = null;

    constructor(container: HTMLElement | string, options?: BimEngine720Options) {
        const el = typeof container === 'string' ? document.getElementById(container) : container;
        if (!el) throw new Error('Container not found');
        this.container = el;

        this.registry = new ManagerRegistry();

        if (options?.locale) localeManager.setLocale(options.locale);
        if (options?.theme && options.theme !== 'custom') {
            themeManager.setTheme(options.theme as 'dark' | 'light');
        }

        // 创建并初始化 720 引擎
        this.engineComponent = new Engine720({
            container: this.container,
            fov: options?.fov,
            enableZoom: options?.enableZoom,
            enableRotate: options?.enableRotate,
            sphereRadius: options?.sphereRadius,
            rotateSpeed: options?.rotateSpeed,
            zoomSpeed: options?.zoomSpeed,
            enableDamping: options?.enableDamping,
            dampingFactor: options?.dampingFactor,
            minFov: options?.minFov,
            maxFov: options?.maxFov,
        }, this.registry);

        this.engineComponent.init();
    }

    // ─── 全景操作 ───

    /** 加载全景图 */
    public async loadPanorama(url: string, options?: PanoramaLoadOptions): Promise<void> {
        return this.engineComponent?.loadPanorama(url, options);
    }

    /** 预加载多个全景图 */
    public async preloadPanoramas(urls: string[]): Promise<void> {
        return this.engineComponent?.preloadPanoramas(urls);
    }

    // ─── 视角控制 ───

    /** 设置视场角 */
    public setFov(fov: number): void {
        this.engineComponent?.setFov(fov);
    }

    /** 获取当前视场角 */
    public getFov(): number {
        return this.engineComponent?.getFov() ?? 75;
    }

    /** 设置相机朝向 */
    public lookAt(phi: number, theta: number, animated?: boolean): void {
        this.engineComponent?.lookAt(phi, theta, animated);
    }

    /** 重置视图 */
    public resetView(): void {
        this.engineComponent?.resetView();
    }

    // ─── 主题 ───

    /** 设置主题 */
    public setTheme(theme: 'dark' | 'light'): void {
        themeManager.setTheme(theme);
    }

    // ─── 事件 ───

    /** 订阅事件 */
    public on<K extends keyof EngineEvents>(event: K, listener: (payload: EngineEvents[K]) => void): () => void {
        return this.registry.on(event, listener);
    }

    /** 取消订阅事件 */
    public off<K extends keyof EngineEvents>(event: K, listener: (payload: EngineEvents[K]) => void): void {
        this.registry.off(event, listener);
    }

    /** 订阅原始 720 引擎事件 */
    public onRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engineComponent?.onRawEvent(event, handler);
    }

    /** 取消订阅原始 720 引擎事件 */
    public offRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engineComponent?.offRawEvent(event, handler);
    }

    // ─── 生命周期 ───

    /** 销毁引擎，释放所有资源 */
    public destroy(): void {
        this.engineComponent?.destroy();
        this.engineComponent = null;
        this.registry.reset();
    }
}
